import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import i18n from '@/i18n';
import AuthService from './AuthService';
import ErrorService from '../core/error-handling/ErrorService';

const AuthContext = createContext(null);

const ta = (key) => i18n.t(key, { ns: 'auth' });

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const initAuth = async (attempt = 1) => {
      try {
        setLoading(true);
        if (AuthService.isAuthenticated()) {
          const currentUser = await AuthService.getCurrentUser();
          if (!cancelled) {
            if (currentUser) {
              setUser(currentUser);
            } else {
              setUser(null);
            }
          }
        } else {
          if (!cancelled) {
            setUser(null);
          }
        }
      } catch (err) {
        if (!cancelled) {
          const isNetworkError = !err.response;
          if (isNetworkError && attempt < 4) {
            // Backend reiniciando: reintentar con backoff (1s, 2s, 3s)
            setTimeout(() => {
              if (!cancelled) initAuth(attempt + 1);
            }, attempt * 1000);
            return;
          }
          // Error de red persistente: mantener sesión activa si el token sigue en localStorage
          if (isNetworkError && AuthService.isAuthenticated()) {
            setLoading(false);
            return;
          }
          // Error real (401 ya lo maneja AuthService, otros errores HTTP)
          ErrorService.logError(err, 'useAuth.initAuth');
          setUser(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    initAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  const checkAuth = async () => {
    try {
      setLoading(true);
      if (AuthService.isAuthenticated()) {
        const currentUser = await AuthService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (err) {
      const isNetworkError = !err.response;
      if (isNetworkError && AuthService.isAuthenticated()) {
        // Error de red transitorio: no desautenticar
        return;
      }
      ErrorService.logError(err, 'useAuth.checkAuth');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const result = await AuthService.login(email, password);

      if (result.success) {
        const userWithPermissions = await AuthService.getCurrentUser();
        setUser(userWithPermissions);
        return { success: true, user: userWithPermissions };
      } else {
        setError(result.error);
        setTimeout(() => {
          setError(null);
        }, 5000);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = ta('errors.unexpectedLogin');
      setError(errorMsg);
      setTimeout(() => {
        setError(null);
      }, 5000);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  const loginWithMicrosoft = useCallback(async (idToken) => {
    try {
      setLoading(true);
      setError(null);

      const result = await AuthService.loginWithMicrosoft(idToken);

      if (result.success) {
        const userWithPermissions = await AuthService.getCurrentUser();
        setUser(userWithPermissions);
        return { success: true, user: userWithPermissions };
      }

      setError(result.error);
      setTimeout(() => {
        setError(null);
      }, 5000);
      return { success: false, error: result.error };
    } catch (err) {
      const errorMsg = ta('errors.unexpectedLoginMicrosoft');
      setError(errorMsg);
      setTimeout(() => {
        setError(null);
      }, 5000);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [setError, setLoading, setUser]);

  const loginWithGoogle = useCallback(async (idToken) => {
    try {
      setLoading(true);
      setError(null);

      const result = await AuthService.loginWithGoogle(idToken);

      if (result.success) {
        const userWithPermissions = await AuthService.getCurrentUser();
        setUser(userWithPermissions);
        return { success: true, user: userWithPermissions };
      }

      setError(result.error);
      setTimeout(() => {
        setError(null);
      }, 5000);
      return { success: false, error: result.error };
    } catch (err) {
      const errorMsg = ta('errors.unexpectedLoginGoogle');
      setError(errorMsg);
      setTimeout(() => {
        setError(null);
      }, 5000);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [setError, setLoading, setUser]);

  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const result = await AuthService.register(userData);

      if (result.success) {
        setUser(result.user);
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = ta('errors.unexpectedRegister');
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await AuthService.logout();
      setUser(null);
      setError(null);
    } catch (err) {
      ErrorService.logError(err, 'useAuth.logout');
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async () => {
    try {
      const currentUser = await AuthService.getCurrentUser();
      setUser(currentUser);
      return currentUser;
    } catch (err) {
      ErrorService.logError(err, 'useAuth.updateUser');
      return null;
    }
  };

  const hasRole = useCallback((role) => {
    if (!user) return false;

    if (user.active_profile?.role?.name === 'superadmin') {
      return true;
    }
    if (user.profiles?.some(p => p.role?.name === 'superadmin' && p.is_active)) {
      return true;
    }

    if (user.active_profile?.role?.name === role) {
      return true;
    }

    if (user.profiles) {
      return user.profiles.some(profile =>
        profile.role?.name === role && profile.is_active
      );
    }

    return user?.roles?.includes(role) || false;
  }, [user]);

  const hasPermission = useCallback((permission) => {
    if (!user) return false;

    if (user.active_profile?.role?.name === 'superadmin') {
      return true;
    }
    if (user.profiles?.some(p => p.role?.name === 'superadmin' && p.is_active)) {
      return true;
    }

    if (!user.permissions || !Array.isArray(user.permissions)) {
      return false;
    }

    return user.permissions.some(p => {
      if (p === permission) return true;

      if (p.endsWith('.*')) {
        const prefix = p.slice(0, -2);
        return permission.startsWith(prefix + '.');
      }

      return false;
    });
  }, [user]);

  // --- Impersonation ---

  const [isImpersonating, setIsImpersonating] = useState(() => AuthService.isImpersonating());
  const [impersonatedUser, setImpersonatedUser] = useState(() => AuthService.getImpersonatedUser());

  const startImpersonation = useCallback(async (accountId) => {
    const result = await AuthService.startImpersonation(accountId);
    if (result.success) {
      setIsImpersonating(true);
      setImpersonatedUser(result.impersonated_user);
      // Reload user data (now showing impersonated user)
      const newUser = await AuthService.getCurrentUser();
      setUser(newUser);
    }
    return result;
  }, []);

  const stopImpersonation = useCallback(async () => {
    const result = await AuthService.stopImpersonation();
    if (result.success) {
      setIsImpersonating(false);
      setImpersonatedUser(null);
      // Reload user data (now showing original admin)
      const adminUser = await AuthService.getCurrentUser();
      setUser(adminUser);
    }
    return result;
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    loginWithMicrosoft,
    loginWithGoogle,
    register,
    logout,
    updateUser,
    checkAuth,
    hasRole,
    hasPermission,
    // Impersonation
    isImpersonating,
    impersonatedUser,
    startImpersonation,
    stopImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(ta('errors.useAuthContext'));
  }

  return context;
};

export default useAuth;
